import { Menu } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import { useMarket, useT } from "../i18n";
import { MobileNavDrawer } from "./MobileNavDrawer";

interface SiteChromeProps {
	children: ReactNode;
	hideNavbar?: boolean;
	onSignOut?: () => void | Promise<void>;
	user: { id?: string } | null;
}

export function SiteChrome({
	children,
	hideNavbar = false,
	onSignOut,
	user,
}: SiteChromeProps) {
	const t = useT();
	const market = useMarket();
	const [mobileNavOpen, setMobileNavOpen] = useState(false);

	return (
		<div className="app-layout">
			{!hideNavbar && (
				<nav className="navbar">
					<Link to="/" className="nav-brand">
						{t.app_name}
					</Link>
					<div className="nav-actions">
						<div className="nav-links">
							{user ? (
								<>
									<Link to="/dashboard" className="nav-link">
										{t.nav_dashboard}
									</Link>
									<button onClick={onSignOut} className="nav-link nav-btn">
										{t.nav_signOut}
									</button>
								</>
							) : (
								<Link to="/auth" className="nav-link">
									{t.nav_signIn}
								</Link>
							)}
						</div>
						<Link to="/wizard" className="nav-link nav-btn-primary">
							{t.landing_primaryCta}
						</Link>
						<button
							type="button"
							className="nav-hamburger"
							aria-controls="mobile-nav-drawer"
							aria-expanded={mobileNavOpen}
							aria-label={t.toolbar_menu}
							onClick={() => setMobileNavOpen(true)}
						>
							<Menu size={20} strokeWidth={2} aria-hidden="true" />
						</button>
					</div>
				</nav>
			)}
			{!hideNavbar && (
				<MobileNavDrawer
					open={mobileNavOpen}
					onClose={() => setMobileNavOpen(false)}
					user={user}
					onSignOut={onSignOut}
				/>
			)}
			<main id="main-content" className="main-content">
				{children}
			</main>
			{!hideNavbar && (
				<footer className="site-footer">
					<div className="footer-inner">
						<div className="footer-col footer-col-brand">
							<span className="footer-brand">{t.footer_brand}</span>
							<p className="footer-tagline">{t.footer_tagline}</p>
						</div>
						<div className="footer-col">
							<h4 className="footer-col-title">{t.footer_colTools}</h4>
							<nav className="footer-col-links" aria-label={t.footer_colTools}>
								<Link to="/wizard" className="footer-link">
									{t.footer_designer}
								</Link>
								<Link to="/calculator/heater" className="footer-link">
									{t.heaterCalc_title}
								</Link>
								<Link to="/calculator/running-cost" className="footer-link">
									{t.runCalc_title}
								</Link>
								{market === "se" && (
									<Link to="/calculator/snow-zone" className="footer-link">
										{t.snowCalc_title}
									</Link>
								)}
							</nav>
						</div>
						<div className="footer-col">
							<h4 className="footer-col-title">{t.footer_colGuides}</h4>
							<nav className="footer-col-links" aria-label={t.footer_colGuides}>
								{market === "se" ? (
									<>
										<Link to="/guide/bastu-kostnad" className="footer-link">
											{t.footer_costGuide}
										</Link>
										<Link to="/guide/bygga-bastu" className="footer-link">
											{t.footer_buildGuide}
										</Link>
									</>
								) : (
									<>
										<Link to="/guide/sauna-cost" className="footer-link">
											{t.footer_costGuide}
										</Link>
										<Link to="/guide/build-a-sauna" className="footer-link">
											{t.footer_buildGuide}
										</Link>
									</>
								)}
								<Link to="/guides" className="footer-link">
									{t.footer_allGuides}
								</Link>
								<a
									href="https://bastuhyra.se"
									className="footer-link"
									target="_blank"
									rel="noopener noreferrer"
								>
									{t.footer_rentalLabel} ↗
								</a>
							</nav>
						</div>
						<div className="footer-col">
							<h4 className="footer-col-title">{t.footer_colCompany}</h4>
							<nav
								className="footer-col-links"
								aria-label={t.footer_colCompany}
							>
								<Link to="/about" className="footer-link">
									{t.footer_about}
								</Link>
								<Link to="/contact" className="footer-link">
									{t.footer_contact}
								</Link>
								<Link to="/press" className="footer-link">
									{t.footer_press}
								</Link>
								<Link to="/privacy" className="footer-link">
									{t.footer_privacy}
								</Link>
							</nav>
						</div>
					</div>
					<div className="footer-bottom">
						<span className="footer-copyright">{t.footer_copyright}</span>
					</div>
				</footer>
			)}
		</div>
	);
}
